"use client";

import type { Address } from "@/types/prisma";
import { Home, Loader2, MapPin, Pencil } from "lucide-react";
import { FormEvent, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";

const EMPTY = { fullName: "", phone: "", line1: "", line2: "" };

export function AddressManager({ initialAddresses }: { initialAddresses: Address[] }) {
  // Only ever 0 or 1 address
  const [address, setAddress] = useState<Address | null>(initialAddresses[0] ?? null);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(!initialAddresses[0]); // open form immediately if none exists
  const [submitting, setSubmitting] = useState(false);

  function startEdit() {
    setForm({
      fullName: address?.fullName ?? "",
      phone: address?.phone ?? "",
      line1: address?.line1 ?? "",
      line2: address?.line2 ?? ""
    });
    setEditing(true);
  }

  function cancel() {
    setEditing(false);
    setForm(EMPTY);
  }

  function set(key: keyof typeof EMPTY, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);

    try {
      // Always POST — the API replaces the single address
      const response = await fetch("/api/addresses", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) throw new Error(data?.message ?? "Unable to save address.");

      setAddress(data.address);
      setEditing(false);
      setForm(EMPTY);
      toast.success("Address saved");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to save address.");
    } finally {
      setSubmitting(false);
    }
  }

  const inputCls =
    "h-11 w-full rounded-md border border-slate-200 bg-transparent px-3 outline-none focus:border-amazon-orange dark:border-white/10";

  return (
    <div className="max-w-xl space-y-5">
      {/* Current address card */}
      {address && !editing && (
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <Home className="mt-0.5 h-5 w-5 shrink-0 text-amazon-teal" />
              <div>
                <p className="font-black text-slate-950 dark:text-white">{address.fullName}</p>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                  {address.line1}
                  {address.line2 ? `, ${address.line2}` : ""}
                </p>
                <p className="mt-0.5 text-sm text-slate-500">{address.phone}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={startEdit}
              className="flex shrink-0 items-center gap-1.5 text-sm font-bold text-amazon-teal hover:underline"
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </button>
          </div>
        </div>
      )}

      {/* Add / Edit form */}
      {editing && (
        <form
          onSubmit={onSubmit}
          className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900"
        >
          <div className="mb-5 flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-xl font-black text-slate-950 dark:text-white">
              <MapPin className="h-5 w-5 text-amazon-orange" />
              {address ? "Edit address" : "Add address"}
            </h2>
            {address && (
              <button type="button" onClick={cancel} className="text-sm font-bold text-amazon-teal hover:underline">
                Cancel
              </button>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-bold">Full name <span className="text-red-500">*</span></label>
              <input
                value={form.fullName}
                onChange={(e) => set("fullName", e.target.value)}
                required
                minLength={2}
                placeholder="Recipient's full name"
                className={inputCls}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-bold">Mobile number <span className="text-red-500">*</span></label>
              <input
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                required
                minLength={8}
                placeholder="+251 91 234 5678"
                type="tel"
                className={inputCls}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-bold">Address line 1 <span className="text-red-500">*</span></label>
              <input
                value={form.line1}
                onChange={(e) => set("line1", e.target.value)}
                required
                minLength={4}
                placeholder="Street, building, area"
                className={inputCls}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-bold">Address line 2</label>
              <input
                value={form.line2}
                onChange={(e) => set("line2", e.target.value)}
                placeholder="Apartment, floor, landmark (optional)"
                className={inputCls}
              />
            </div>
          </div>

          <div className="mt-6 flex gap-3">
            <Button type="submit" disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Save address
            </Button>
            {address && (
              <Button type="button" variant="outline" onClick={cancel}>
                Cancel
              </Button>
            )}
          </div>
        </form>
      )}

      {/* Empty state */}
      {!address && !editing && (
        <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center dark:border-white/20">
          <MapPin className="mx-auto h-8 w-8 text-slate-400" />
          <p className="mt-3 font-bold text-slate-700 dark:text-slate-200">No address saved yet</p>
          <Button className="mt-4" onClick={() => setEditing(true)}>
            Add address
          </Button>
        </div>
      )}
    </div>
  );
}
